import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogTitle, IconButton, Input, InputAdornment, Tooltip, Avatar, Typography, Switch, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { capitalize } from "../../utils/helpers";
import errorHandler from "../../utils/errorHandler";
import { EditOutlined, Visibility as VisibilityIcon } from "@mui/icons-material";
import { apiCategory } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import TableFilter from "../../components/shared/TableFilter";
import { IMAGE_BASE, STATUS_OPTIONS_LIST } from "../../utils/constants";
import { fDate } from "../../utils/formatTime";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CustomPagination from "../../components/CustomPagination";
import { useBulkSelect } from "../../hooks/useBulkSelect";

export default function Category() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(""); 
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);

  const {
    selectedRows,
    getCheckboxColumn,
    clearSelection,
  } = useBulkSelect(data, null, [debouncedSearch, currentPage, pageSize, refresh, status]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleClose = () => setShow(false);

  const columns = [
    getCheckboxColumn(),
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Image",
      cell: (row) => (
        <Avatar
          src={row.image ? IMAGE_BASE + row.image : ""}
          alt={row.name}
          sx={{ width: 50, height: 50, border: "2px solid #e2e8f0" }}
        />
      ),
      sortable: false,
    },
    {
      name: "Category Name",
      sortable: true,
      selector: (row) => capitalize(row.name),
    },
    {
      name: "Description",
      sortable: false,
       selector: (row) => capitalize(row.description),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
    
    },
    {
      name: "Status",
      cell: (row) => (
        <Switch
          checked={row.status === 1}
          onClick={() => {
            setShow(true);
            setCurrentStatus(row.status);
            setStatusId(row._id);
            setAction("status");
            setWarning(
              `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"} this main category?`
            );
          }}
          inputProps={{ "aria-label": "controlled" }}
          color="primary"
        />
      ),
    },
    {
      name: "Actions",
      center: true,
      width: "180px",
      cell: (row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="Manage Subcategories">
            <IconButton 
              onClick={() => navigate(`/category/view/${row?._id}`)}
              sx={{ color: "primary.main" }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Main Category">
            <IconButton 
              onClick={() => navigate(`/category/edit/${row?._id}`)}
              sx={{ color: "primary.main" }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Main Category">
            <IconButton 
              onClick={() => deleteData(row?._id)}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const deleteData = async (ids) => {
    const isMultiple = Array.isArray(ids);
    Swal.fire({
      text: isMultiple 
        ? `Are you sure you want to delete ${ids.length} selected category(ies)?`
        : "Are you sure you want to delete this Category?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const categoryIds = isMultiple ? ids : [ids];
          const payload = categoryIds.length === 1 
            ? { id: categoryIds[0] }
            : { id: categoryIds };
          
          const res = await apiCategory.delete(payload);
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            if (!isMultiple) {
              setData((prevData) => prevData.filter((item) => item._id !== ids));
              setTotalCount((prevTotal) => prevTotal - 1);
            } else {
              setData((prevData) => prevData.filter((item) => !ids.includes(item._id)));
              setTotalCount((prevTotal) => Math.max(0, prevTotal - ids.length));
            }
            clearSelection();
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          console.error("Error deleting...", error);
          errorHandler(error);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      deleteData(selectedRows);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem); 
    }, 500); 
    return () => clearTimeout(delayDebounceFn); 
  }, [searchItem]);

  useEffect(() => {
    const body = {
      searchItem: debouncedSearch, 
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };

    setIsLoading(true);
    setLoaderDelay(false); 

    apiCategory.getAll(body)
      .then((res) => {
        if (res.status === 200) {
          // Map API response fields to table data structure here
          const categoriesRaw = res?.data?.data?.categories || [];
          const categories = categoriesRaw.map((item) => ({
            _id: item._id,
            name: item.name,
            description: item.description,
            image: item.categoryImage,
            subcategoryCount: 0,
            totalListings: item.listingCount,
            status: item.status,
            createdAt: item.createdAt,
          }));
          setTimeout(() => {
            setData(categories);
            setTotalCount(res?.data?.data?.total || res?.data?.data?.totalItems || 0);
            setLoaderDelay(true); 
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        errorHandler(err);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false); 
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status]);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await apiCategory.changeStatus({ id, status: newStatus });
      setCurrentStatus("");

      if (res.status === 200 && res?.data?.message) {
        toast.success(res?.data?.message, { toastId: "success1" });
        setRefresh(!refresh);
        setShow(false);
      } else {
        toast.error(res?.data?.message, { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  return (
    <Card>
      <CardHeader titleTypographyProps={{ variant: "h4" }} title="Main Categories" />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "flex-end",
          p: 2,
          gap: 2,
        }}
      >
        <TableFilter data={STATUS_OPTIONS_LIST} value={status} setValue={setStatus} filterName="Status" />
        <Input
          label="Search"
          variant="standard"
          value={searchItem}
          placeholder="Search by name"
          onChange={(e) => setSearchItem(e.target.value)}
          sx={{
            minWidth: { xs: "100%", sm: "250px" },
            maxWidth: { xs: "100%", sm: "300px" },
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton aria-label="clear-search" onClick={() => setSearchItem("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          onClick={() => navigate("/category/add")}
        
        >
          Add Category
        </Button>
      </Box>
      <CardContent>
        <DataTable
            customStyles={{
              headCells: {
                style: {
                  fontSize: "15px",
                  fontWeight: "bold",
                },
              },
            }}
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            onChangePage={handlePageChange}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 20, 50]}
            onChangeRowsPerPage={(rows) => {
              setPageSize(rows);
              setCurrentPage(1);
            }}
            progressPending={isLoading || !loaderDelay}
            progressComponent={<Loader />}
            paginationComponent={(props) => (
              <CustomPagination {...props} currentPage={currentPage} />
            )}
            className="custom"
          />
      </CardContent>
      <Dialog
        open={show}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        className="popupbox-statis"
      >
        <DialogTitle as="h3">{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button variant="outlined" onClick={() => { if (action === "status") { toggleChecked(statusId, currentStatus); } }}>Yes</Button>
          <Button variant="outlined" onClick={handleClose}>No</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
